import React, { createContext, useContext, useEffect } from "react";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";
import Grid from "@mui/material/Unstable_Grid2";
import { ToggleButton, ToggleButtonGroup } from "@mui/material";
import {
  StyledInputTime,
  StyledToolBarDate,
  StyledInputTimeColon,
  StyledInputWrapper,
  StyledInputLabel,
} from "./styles";

type PeriodT = "am" | "pm";

export type ToolBarContextT = {
  value?: string | Dayjs;
  hideTime: boolean;
  onTimeChange: (value: string) => void;
};

export const ToolBarContext = createContext<ToolBarContextT>({
  hideTime: true,
  onTimeChange: () => undefined,
});

const to24Hour = (hour: number, period: PeriodT) => (hour % 12) + (period === "pm" ? 12 : 0);

const clampToMax = (event: React.ChangeEvent<HTMLInputElement>) =>
  Math.min(parseInt(event.target.value, 10), parseInt(event.target.max, 10));

const ToolBarComponent = () => {
  const { value, hideTime, onTimeChange } = useContext(ToolBarContext);
  const selectedDateTime = dayjs(value);
  const period: PeriodT = selectedDateTime.hour() >= 12 ? "pm" : "am";

  useEffect(() => {
    if (hideTime) return;
    const normalized = dayjs(value).toString();
    if (normalized !== value) onTimeChange(normalized);
  }, [value, hideTime, onTimeChange]);

  const updateTime = (hour: number, minute: number) => {
    onTimeChange(dayjs(value).hour(hour).minute(minute).toString());
  };

  const handleHourChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const hour = clampToMax(event);
    updateTime(to24Hour(_.isNaN(hour) ? 1 : hour, period), selectedDateTime.minute());
  };

  const handleMinuteChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const minute = clampToMax(event);
    updateTime(selectedDateTime.hour(), _.isNaN(minute) ? 1 : minute);
  };

  const handlePeriodChange = (_event: React.MouseEvent<HTMLElement>, nextPeriod: PeriodT | null) => {
    if (!nextPeriod) return;
    updateTime(to24Hour(selectedDateTime.hour(), nextPeriod), selectedDateTime.minute());
  };

  return (
    <Grid container maxWidth="395px">
      <Grid xs={hideTime ? 12 : 11} xsOffset={hideTime ? 0 : 1}>
        <StyledToolBarDate sx={{ height: "40px", paddingTop: "20px", paddingBottom: "38px" }}>
          {dayjs(value || new Date())
            .format("ddd, MMM D")
            .toString()}
        </StyledToolBarDate>
      </Grid>
      {!hideTime && (
        <Grid xsOffset={1} sx={{ marginRight: "12px" }}>
          <StyledInputWrapper>
            <StyledInputTime
              type="number"
              min="1"
              max="12"
              defaultValue={parseInt(selectedDateTime.format("hh"), 10)}
              onChange={handleHourChange}
            />
            <StyledInputLabel>Hour</StyledInputLabel>
          </StyledInputWrapper>
          <StyledInputTimeColon>:</StyledInputTimeColon>
          <StyledInputWrapper>
            <StyledInputTime
              type="number"
              min="1"
              max="59"
              defaultValue={selectedDateTime.format("mm")}
              onChange={handleMinuteChange}
            />
            <StyledInputLabel>Minute</StyledInputLabel>
          </StyledInputWrapper>
        </Grid>
      )}
      {!hideTime && (
        <Grid xs={1}>
          <ToggleButtonGroup
            value={period}
            onChange={handlePeriodChange}
            sx={{
              "& .MuiToggleButton-root": {
                height: "30px",
                background: "var(--cotton-boll)",
                color: "var(--nepal-blue)",
              },
              "& .Mui-selected": {
                background: "linear-gradient(270.54deg, var(--picton-blue) 49.88%, var(--blue-ribbon) 135.73%)",
                color: "white",
              },
            }}
            orientation="vertical"
            exclusive
          >
            <ToggleButton value="am">AM</ToggleButton>
            <ToggleButton value="pm">PM</ToggleButton>
          </ToggleButtonGroup>
        </Grid>
      )}
    </Grid>
  );
};

export default ToolBarComponent;
